import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import styles from './SubtitlesControl.module.css';
import {cc_captions, check} from '../../../assets/images';
import {tapyblPlayerText} from '../tapyblPlayerText';
import featureFlags, {FeatureFlagName} from '../../../utils/featureFlags';
import {SubtitleTrack} from '../subtitles';

type UiMode = 'small' | 'big';

// .CcMenu's CSS heights, the least it shrinks to, and the gap kept below the
// player's top edge.
const MENU_MAX_HEIGHT = 320;
const MENU_MIN_HEIGHT = 127;
const MENU_SMALLEST_HEIGHT = 48;
const MENU_TOP_GAP = 8;

interface SubtitlesControlProps {
  /** Every language the video has subtitles in; none hides the control. */
  subtitleTracks: SubtitleTrack[];
  /** The language shown, or null when captions are off. */
  subtitleLanguage: string | null;
  setSubtitleLanguage: (language: string | null) => void;
  uiMode: UiMode;
}

const SubtitlesControlComponent: React.FC<SubtitlesControlProps> = ({
  subtitleTracks,
  subtitleLanguage,
  setSubtitleLanguage,
  uiMode,
}) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuMaxHeight, setMenuMaxHeight] = useState<number>();
  const subtitlesFeatureEnabled = featureFlags.get(FeatureFlagName.Subtitles);

  // The player clips its overflow, so a long language list would lose its
  // title and Off: the open menu is no taller than the space between its
  // bottom edge and the player's top, and its list scrolls.
  useLayoutEffect(() => {
    if (!menuVisible) return;
    const fit = () => {
      const menu = menuRef.current;
      const player = containerRef.current?.closest('.TapyblPlayerContainer');
      if (!menu || !player) {
        setMenuMaxHeight(undefined);
        return;
      }
      const space =
        menu.getBoundingClientRect().bottom -
        player.getBoundingClientRect().top -
        MENU_TOP_GAP;
      setMenuMaxHeight(
        Math.max(
          MENU_SMALLEST_HEIGHT,
          Math.floor(
            Math.min(MENU_MAX_HEIGHT, window.innerHeight * 0.6, space),
          ),
        ),
      );
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [menuVisible, subtitleTracks.length]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setMenuVisible(false);
      }
    };

    if (menuVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuVisible]);

  const toggleMenu = () => {
    setMenuVisible(prev => !prev);
  };
  const selectCc = (language: string | null) => {
    setSubtitleLanguage(language);
    setMenuVisible(false);
  };

  if (subtitleTracks.length === 0 || !subtitlesFeatureEnabled) return null;

  return (
    <div ref={containerRef} className={styles.CcWrapper}>
      <div
        ref={menuRef}
        className={`${styles.CcMenu} ${
          menuVisible ? styles.visible : styles.hidden
        }`}
        style={
          menuMaxHeight === undefined
            ? undefined
            : {
                maxHeight: menuMaxHeight,
                minHeight: Math.min(MENU_MIN_HEIGHT, menuMaxHeight),
              }
        }>
        <div className={styles.CcMenuTitle}>{tapyblPlayerText.captions}</div>
        <div className={styles.CcMenuItems} role="menu">
          <div
            className={styles.CcMenuItem}
            role="menuitemradio"
            aria-checked={subtitleLanguage === null}
            onClick={() => selectCc(null)}>
            {tapyblPlayerText.off}
            {subtitleLanguage === null ? (
              <img src={check} alt="CC off" className={styles.CheckIcon} />
            ) : null}
          </div>
          {subtitleTracks.map(track => (
            <div
              key={track.language}
              className={styles.CcMenuItem}
              role="menuitemradio"
              aria-checked={subtitleLanguage === track.language}
              lang={track.language}
              onClick={() => selectCc(track.language)}>
              {track.label}
              {subtitleLanguage === track.language ? (
                <img
                  src={check}
                  alt={`${track.label} on`}
                  className={styles.CheckIcon}
                />
              ) : null}
            </div>
          ))}
        </div>
      </div>
      <button
        type="button"
        className={
          uiMode === 'small' ? styles.SmallCcButton : styles.BigCcButton
        }
        onClick={toggleMenu}
        aria-pressed={subtitleLanguage !== null}>
        <img src={cc_captions} alt="CC Icon" />
        <span>{tapyblPlayerText.captions}</span>
      </button>
    </div>
  );
};

const SubtitlesControl = React.memo(SubtitlesControlComponent);

export default SubtitlesControl;
